/**
 * @file EscalationDetails.tsx
 * @deprecated Copied to app/modules/procurement/pages/escalations/EscalationDetails.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/escalations/EscalationDetails
 * @description Escalation detail view showing full escalation information, metadata, 
 *   action history, and resolution workflow. Supports status transitions (Pending → 
 *   Resolved/Rejected) with comment modal for resolution notes. Displays escalation 
 *   type-specific details and linked shipment information.
 * 
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo [P1] Implement fetchEscalationsInfo() error handling
 *   - Wrap API call in try/catch
 *   - Show error toast if escalation ID not found
 *   - Redirect to list on 404 error
 *   - Reference: purchase-order-details-v2.tsx error pattern
 * 
 * @todo [P1] Implement escalation resolution workflow
 *   - handleResolve() - POST resolve action with optional resolution_notes
 *   - handleReject() - POST reject action with rejection_reason
 *   - Update local details state after action succeeds
 *   - Show confirmation toast before state transition
 *   - Reference: uprocure escalation details action handlers
 * 
 * @todo [P2] Add comment timeline/history view
 *   - Fetch escalation comments from API
 *   - Display comment thread showing all resolution attempts
 *   - Show comments from staff and system (e.g., status changes)
 *   - Implement proper timestamp formatting
 *   - Reference: uprocure escalation details comment section
 * 
 * @todo [P2] Implement linked shipment navigation
 *   - Display shipment reference from escalation data
 *   - Add link to navigate to shipment/PO details
 *   - Show shipment status and relevant details inline
 *   - Reference: purchase-order-details-v2.tsx shipment linking
 * 
 * @todo [P2] Add audit logging for resolution actions
 *   - Log who resolved/rejected escalation and when
 *   - Include resolution notes in audit trail
 *   - Sync with _auditLog platform on backend
 *   - Reference: add-purchase-order.tsx audit logging pattern
 * 
 * @todo [P3] Add permission-based action visibility
 *   - Show resolve/reject buttons only if user has escalation edit permission
 *   - Lock actions if escalation is already resolved
 *   - Show "view only" message for read-only users
 *   - Reference: po-management edit field locking pattern
 */

import { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import threeDot from "../../../../_admin/assets/media/svg_uTrade/three-dot-round.svg";
import APICallService from "../../../../api/apiCallService";
import { ESCALATIONS } from "../../../../api/apiEndPoints";
import Loader from "../../../../Global/loader";
import { CustomSelectTable } from "../../../custom/Select/CustomSelectTable";
import EscalationCommentModal from "../../../modals/escalation-comment-modal";

const escalationStatusMeta: any = {
    1: { label: "In Progress", badge: "warning" },
    2: { label: "Resolved", badge: "success" },
    3: { label: "Rejected", badge: "danger" },
};

const escalationTypeStatusMeta: any = {
    1: { label: "Pending Approval" },
    2: { label: "Delivery Delay" },
    3: { label: "Payment Delay" },
};

const EscalationDetails = () => {
    const navigate = useNavigate();
    const { state }: any = useLocation();
    const [initLoader, setInitLoader] = useState(false);
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(false);

    const [details, setDetails] = useState<any>(null);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<
        "resolve" | "reject" | null
    >(null);

    useEffect(() => {
        setInitLoader(true);
        fetchEscaltionsInfo();
        setInitLoader(false);
    }, [state?.id]);

    const statusInfo = escalationStatusMeta[details?.status] || {};
    const typeInfo = escalationTypeStatusMeta[details?.type] || {};

    const fetchEscaltionsInfo = async () => {
        setLoading(true);
        let apiService = new APICallService(ESCALATIONS.escalationInfo, state.id);
        let response = await apiService.callAPI();
        if (response) {
            setDetails(response);
        } else {
            setDetails(null);
        }
        setLoading(false);
    };

    const openMenuOnClick = async (id: any) => {
        setShow(true);
    };
    const onMenuClose = async () => {
        setShow(false);
    };
    const onMenuOpen = async (id: any) => {
        setShow(true);
    };

    const handleOption = async (event: any, data: any) => {
        if (!details) {
            return;
        }

        if (event.value === 1) {
            setPendingAction("resolve");
            setShowCommentModal(true);
        } else if (event.value === 2) {
            setPendingAction("reject");
            setShowCommentModal(true);
        }
    };

    const handleCommentSubmit = async (data: {
        comment: string;
        action: "resolve" | "reject";
    }) => {
        let payload: any = {
            status: data.action === "resolve" ? 2 : 3,
            comments: data.comment,
        };
        let apiService = new APICallService(ESCALATIONS.updateStatus, payload, {
            id: state.id,
        });
        let response = await apiService.callAPI();
        if (response) {
            setShowCommentModal(false);
            navigate("/UProcure/escalations");
        }
    };

    if (initLoader || loading || !details) {
        return (
            <div className="d-flex justify-content-center p-10">
                <Loader loading={initLoader || loading} />
            </div>
        );
    }

    return (
        <>
            <div className="p-5">
                <EscalationCommentModal
                    show={showCommentModal}
                    onHide={() => setShowCommentModal(false)}
                    onSubmit={handleCommentSubmit}
                    action={pendingAction || "resolve"}
                />

                {/* Header Section */}
                <Row className="align-items-center g-3 mb-6">
                    <Col xs>
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            <h1 className="fs-22 fw-bolder mb-0">Escalation Details</h1>
                            <span
                                className={`badge bg-${statusInfo.badge || "secondary"
                                    } fs-14 fw-700 px-3 py-2`}
                            >
                                {statusInfo.label || "Pending"}
                            </span>
                            <span className="badge bg-light text-dark border fs-14 fw-600 px-3 py-2">
                                Ref: {details.refKey}
                            </span>
                        </div>
                    </Col>
                    <Col xs="auto" className="d-flex gap-3">
                        <Button
                            variant="primary"
                            size="sm"
                            className="uw-btn uw-btn-primary uw-btn-sm fw-700"
                            onClick={() => {
                                navigate("/UProcure/purchase-order-management/purchase-order-details", 
                                    {
                                      state: {
                                        id: details?.shipment?._id,
                                      },
                                    }
                                );
                            }}
                        >
                            View PO
                        </Button>
                        {/* <Button
                            variant="light-primary"
                            size="sm"
                            className="fw-700"
                            onClick={() => {
                                navigate("/Uprocure/escalations");
                            }}
                        >
                            Back to Escalations
                        </Button> */}
                        {details?.status === 1 && (
                            <CustomSelectTable
                                marginLeft={"0px"}
                                menuMargin={"-60px"}
                                width={"140px"}
                                placeholder={
                                    <img
                                        className="img-fluid"
                                        width={47}
                                        height={47}
                                        src={threeDot}
                                        alt=""
                                    />
                                }
                                options={[
                                    {
                                        label: (
                                            <button className="btn btn-link fs-12 fw-400 text-success ms-3 p-4">
                                                Mark as Resolved
                                            </button>
                                        ),
                                        value: 1,
                                    },
                                    {
                                        label: (
                                            <button className="btn btn-link fs-12 fw-400 text-danger ms-3 p-4">
                                                Reject Escalation
                                            </button>
                                        ),
                                        value: 2,
                                    },
                                ]}
                                backgroundColor="white"
                                show={show}
                                onMenuClose={onMenuClose}
                                openMenuOnClick={openMenuOnClick}
                                onMenuOpen={onMenuOpen}
                                onChange={(event: any) => {
                                    handleOption(event, details);
                                }}
                            />
                        )}
                    </Col>
                </Row>

                {/* Escalation Information Card */}
                <Row className="mb-5">
                    <Col xs={12}>
                        <Card className="card-bordered">
                            <Card.Header className="bg-light align-items-center">
                                <Card.Title className="fs-20 fw-bolder mb-0">
                                    Escalation Information
                                </Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Col md={5} xs={4}>
                                        <label className="fs-16 fw-500 text-dark">
                                            Reference Key:
                                        </label>
                                    </Col>
                                    <Col md={7} xs={8}>
                                        <span className="fw-bold fs-16 fw-600 text-dark">
                                            {details.refKey}
                                        </span>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={5} xs={4}>
                                        <label className="fs-16 fw-500 text-dark">Raised By:</label>
                                    </Col>
                                    <Col md={7} xs={8}>
                                        <span className="fw-bold fs-16 fw-600 text-dark">
                                            {details.raisedBy?.name}
                                        </span>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={5} xs={4}>
                                        <label className="fs-16 fw-500 text-dark">Status:</label>
                                    </Col>
                                    <Col md={7} xs={8}>
                                        <span
                                            className={`badge bg-${statusInfo.badge || "secondary"
                                                } fs-14 fw-700 px-3 py-2`}
                                        >
                                            {statusInfo.label || "Pending"}
                                        </span>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={5} xs={4}>
                                        <label className="fs-16 fw-500 text-dark">Type:</label>
                                    </Col>
                                    <Col md={7} xs={8}>
                                        <span className="fw-bold fs-16 fw-600 text-dark">
                                            {typeInfo.label || "Pending Approval"}
                                        </span>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={5} xs={4}>
                                        <label className="fs-16 fw-500 text-dark">
                                            PO Id:
                                        </label>
                                    </Col>
                                    <Col md={7} xs={8}>
                                        <span className="fw-bold fs-16 fw-600 text-dark">
                                            {details.shipment?.refKey || "-"}
                                        </span>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={5} xs={4}>
                                        <label className="fs-16 fw-500 text-dark">
                                            Reason / Subject:
                                        </label>
                                    </Col>
                                    <Col md={7} xs={8}>
                                        <span className="fs-14 fw-500 text-dark">
                                            {details.reason}
                                        </span>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Attachments Section */}
                {details.attachments && details.attachments.length > 0 && (
                    <Row className="mb-5">
                        <Col xs={12}>
                            <Card className="card-bordered">
                                <Card.Header className="bg-light align-items-center">
                                    <Card.Title className="fs-20 fw-bolder mb-0">
                                        Attachments
                                    </Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-4 align-items-stretch">
                                        {details.attachments.map(
                                            (attachment: string, idx: number) => (
                                                <Col md={4} key={idx}>
                                                    <div className="h-100 d-flex flex-column justify-content-between">
                                                        <div className="image-input image-input-outline d-flex flex-center border bg-white rounded w-lg-200px h-200px p-lg-3 p-2 mx-auto">
                                                            <div
                                                                className="image-input-wrapper shadow-none bgi-contain w-100 h-100 bgi-position-center d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    backgroundImage: `url(/${attachment})`,
                                                                }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </Col>
                                            )
                                        )}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Comments/Activity Section */}
                {details.comments?.message && (
                    <Row className="mb-5">
                        <Col xs={12}>
                            <Card className="card-bordered">
                                <Card.Header className="bg-light align-items-center">
                                    <Card.Title className="fs-20 fw-bolder mb-0">
                                        Comments & Updates
                                    </Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="mb-3">
                                        <Col md={5} xs={4}>
                                            <label className="fs-16 fw-500 text-dark">
                                                Commented By:
                                            </label>
                                        </Col>
                                        <Col md={7} xs={8}>
                                            <span className="fw-bold fs-16 fw-600 text-dark">
                                                {details.comments.user?.name}
                                            </span>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col md={5} xs={4}>
                                            <label className="fs-16 fw-500 text-dark">Comment:</label>
                                        </Col>
                                        <Col md={7} xs={8}>
                                            <span className="fs-14 fw-500 text-dark">
                                                {details.comments.message}
                                            </span>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        </>
    );
};

export default EscalationDetails;
